import { createContext, use, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { jsx } from "react/jsx-runtime";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
//#region src/radio-group/RadioGroup.tsx
const RadioGroupContext = createContext({
	size: "md",
	variant: "none"
});
const RadioGroup = ({ size = "md", variant = "none", className, children, ...props }) => {
	const contextValue = useMemo(() => ({
		size,
		variant
	}), [size, variant]);
	return /* @__PURE__ */ jsx(RadioGroupContext, {
		value: contextValue,
		children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Root, {
			className: twMerge((variant === "none" || variant === "card") && "flex flex-col gap-y-2", variant === "toggle" && "flex flex-wrap items-stretch rounded-xl bg-gray-100 p-1", variant === "card" && "sm:flex-row sm:gap-x-2", className),
			...props,
			children
		})
	});
};
const RadioGroupItem = ({ id, value, className, children, size, variant, ...rest }) => {
	const ctx = use(RadioGroupContext);
	const finalSize = size ?? ctx.size;
	const finalVariant = variant ?? ctx.variant;
	const contextValue = useMemo(() => ({
		size: finalSize,
		variant: finalVariant
	}), [finalSize, finalVariant]);
	return /* @__PURE__ */ jsx(RadioGroupContext, {
		value: contextValue,
		children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Item, {
			...rest,
			id: id ?? (value === "" ? void 0 : value),
			value,
			className: twMerge("group cursor-pointer", "radix-disabled:cursor-not-allowed radix-disabled:bg-gray-50 radix-disabled:text-gray-500 radix-disabled:radix-state-checked:border-gray-300 radix-disabled:radix-state-checked:bg-gray-300", finalVariant === "none" && [
				"flex shrink-0 items-center justify-center",
				"rounded-full border border-gray-300 bg-white",
				"radix-state-checked:border-blue-500 radix-state-checked:bg-blue-500",
				finalSize === "sm" && "h-3 w-3",
				finalSize === "md" && "h-4 w-4",
				finalSize === "lg" && "h-5 w-5"
			], finalVariant === "card" && [
				"relative w-full text-left",
				"rounded-xl border-none bg-white p-4 ring-1 ring-gray-300",
				"radix-state-checked:ring-2 radix-state-checked:ring-blue-500 radix-state-checked:ring-inset"
			], finalVariant === "toggle" && [
				"flex items-center justify-center text-center whitespace-normal",
				"h-auto flex-1 rounded-lg bg-transparent font-medium text-gray-600",
				finalSize === "sm" ? "min-h-8 px-3 py-1 text-sm" : "min-h-10 px-4 py-2 text-base",
				"radix-state-checked:bg-white radix-state-checked:text-black radix-state-checked:drop-shadow-xs"
			], finalVariant === "custom" && null, className),
			children: children || /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, {})
		})
	});
};
const RadioGroupItemContainer = ({ className, children, ...rest }) => {
	return /* @__PURE__ */ jsx("div", {
		className: twMerge("flex items-start gap-2", "group-radix-disabled:opacity-50", className),
		...rest,
		children
	});
};
const RadioGroupIndicatorContainer = ({ className, children, ...rest }) => {
	const { size, variant } = use(RadioGroupContext);
	return /* @__PURE__ */ jsx("div", {
		className: twMerge(variant !== "custom" && "mt-1.5", variant === "card" && "mt-0.5", "flex shrink-0 items-center justify-center rounded-full", "border border-gray-300", "group-radix-state-checked:border-blue-500 group-radix-state-checked:bg-blue-500", "group-radix-disabled:cursor-not-allowed group-radix-disabled:opacity-50", size === "sm" && "h-3 w-3", size === "md" && "h-4 w-4", size === "lg" && "h-5 w-5", className),
		...rest,
		children
	});
};
const RadioGroupIndicator = ({ className, ...rest }) => {
	const { size } = use(RadioGroupContext);
	return /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, {
		className: twMerge("flex items-center justify-center", "rounded-full bg-white", "radix-disabled:bg-white", size === "sm" && "h-1 w-1", size === "md" && "h-1.5 w-1.5", size === "lg" && "h-2 w-2", className),
		...rest
	});
};
const Root = RadioGroup;
const Item = RadioGroupItem;
const ItemContainer = RadioGroupItemContainer;
const Indicator = RadioGroupIndicator;
const IndicatorContainer = RadioGroupIndicatorContainer;
//#endregion
export { Indicator, IndicatorContainer, Item, ItemContainer, RadioGroup, RadioGroupIndicator, RadioGroupIndicatorContainer, RadioGroupItem, RadioGroupItemContainer, Root };
